/**
 * Memory ledger - what the page was carrying when the content process died.
 *
 * iOS kills the WebView's content process under memory pressure and Capacitor
 * reloads the page; the user sees a black frame and lands on the same URL.
 * Nothing survives the kill except storage, so the ledger is written ahead of
 * time — on every stack change — and read back on the reload that follows.
 * The report goes through the analytics and error channels that already exist.
 *
 * NORTHSTARS:
 * - Invent nothing - sessionStorage, PostHog, Sentry
 * - Event-driven, no sampler
 * - Numbers, not guesses - the native budget is the truth, the rest are proxies
 */

import * as Sentry from "@sentry/react";
import { analytics } from "@/services/analytics.service";
import { StashtMemory } from "@/plugins/StashtMemory";
import { isNativeApp } from "@/platform";
import { countLiveMapContexts } from "@/lib/map/mapResidency";

export const STACK_LEDGER_KEY = "stasht_stack_ledger";
/** What the last boot decided about the stored stack; read back with the ledger. */
export const STACK_RESTORE_KEY = "stasht_stack_restore";
/** Written by lazy-with-retry when the app reloads itself for fresh chunks. */
const OWN_RELOAD_KEY = "chunk_error_refresh";
const OWN_RELOAD_WINDOW_MS = 15_000;

export interface StackLedger {
  at: number;
  route: string;
  depth: number;
  /** Decoded pixels of every <img> in the document, at natural size — an upper bound. */
  decodedImageMB: number;
  /** Mapbox canvases alive in the document; their GPU memory is invisible to the image count. */
  liveMapContexts: number;
  availableMB: number | null;
}

export const measureDecodedImageMB = (): number => {
  if (typeof document === "undefined") return 0;
  let bytes = 0;
  for (const image of Array.from(document.images)) {
    if (image.complete && image.naturalWidth > 0) bytes += image.naturalWidth * image.naturalHeight * 4;
  }
  return Math.round(bytes / 1e6);
};

export const recordStackLedger = async ({ route, depth }: { route: string; depth: number }): Promise<void> => {
  const decodedImageMB = measureDecodedImageMB();
  const liveMapContexts = countLiveMapContexts();
  let availableMB: number | null = null;
  try {
    const { availableBytes } = await StashtMemory.availableMemory();
    availableMB = availableBytes === null ? null : Math.round(availableBytes / 1e6);
  } catch {
    // Web, or an old shell without the plugin: the ledger just has no budget.
  }
  const ledger: StackLedger = { at: Date.now(), route, depth, decodedImageMB, liveMapContexts, availableMB };
  try {
    sessionStorage.setItem(STACK_LEDGER_KEY, JSON.stringify(ledger));
  } catch {
    // Storage unavailable: nothing to report later, nothing to break now.
  }
};

export interface StackRestoreRecord {
  at: number;
  href: string;
  restoredDepth: number;
  restoredTop: string | null;
}

export const recordStackRestore = (record: Omit<StackRestoreRecord, "at">): void => {
  try {
    sessionStorage.setItem(STACK_RESTORE_KEY, JSON.stringify({ at: Date.now(), ...record }));
  } catch {
    // Nothing to diagnose later; nothing to break now.
  }
};

/**
 * A document load the app did not ask for. In the native shell the only
 * document navigations are the cold launch ("navigate") and the app's own
 * chunk refresh ("reload" with its marker); anything else — a "reload"
 * without the marker, or WebKit restoring the session item after the content
 * process died, which reports as "back_forward" — is the process having
 * been replaced.
 */
export const detectUnexpectedReload = (): boolean => {
  if (typeof performance === "undefined") return false;
  const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  if (!navigation) return false;
  if (navigation.type === "back_forward") return true;
  if (navigation.type !== "reload") return false;
  let ownReloadAt = 0;
  try {
    ownReloadAt = Number(sessionStorage.getItem(OWN_RELOAD_KEY) || 0);
  } catch {
    ownReloadAt = 0;
  }
  return !(ownReloadAt && Date.now() - ownReloadAt < OWN_RELOAD_WINDOW_MS);
};

export const readStackLedger = (): StackLedger | null => {
  try {
    const raw = sessionStorage.getItem(STACK_LEDGER_KEY);
    return raw ? (JSON.parse(raw) as StackLedger) : null;
  } catch {
    return null;
  }
};

/**
 * Report a reload the app did not ask for. `ledger` must be read before the
 * app renders: the navigation stack rewrites it as soon as it mounts, and the
 * one that matters is what the page was carrying when it died.
 */
export const reportUnexpectedReload = (ledger: StackLedger | null): void => {
  if (!isNativeApp() || !detectUnexpectedReload()) return;
  let restore: StackRestoreRecord | null = null;
  try {
    const raw = sessionStorage.getItem(STACK_RESTORE_KEY);
    restore = raw ? (JSON.parse(raw) as StackRestoreRecord) : null;
  } catch {
    restore = null;
  }
  const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  const properties: Record<string, unknown> = {
    route: window.location.pathname,
    navigation_type: navigation?.type ?? null,
    restored_depth: restore?.restoredDepth ?? null,
    restored_top: restore?.restoredTop ?? null,
    ledger_route: ledger?.route ?? null,
    ledger_depth: ledger?.depth ?? null,
    ledger_decoded_image_mb: ledger?.decodedImageMB ?? null,
    ledger_live_map_contexts: ledger?.liveMapContexts ?? null,
    ledger_available_mb: ledger?.availableMB ?? null,
    ledger_age_s: ledger ? Math.round((Date.now() - ledger.at) / 1000) : null,
  };
  analytics.capture("webcontent_terminated", properties);
  Sentry.captureMessage("WebContent process terminated; page reloaded", { level: "warning", extra: properties });
};
